<template>
  <v-card>
    <v-container>
      <v-skeleton-loader
        :loading="skeleton"
        class="mx-auto"
        type="card-heading, list-item-two-line, card-heading, actions"
      >
        <v-row>
          <v-col>
            <v-icon> mdi-briefcase </v-icon>
            <span class="headline mx-2">Employment</span>
            <employee-separations-dialog-list
              v-if="!isMyProfile"
              :employee-uid="employeeUid"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col v-if="isResigned">            
            <span class="font-weight-medium error--text text--darken-2"> 
              **** {{ employee.current_separation ? employee.current_separation.type.name : 'RESIGNED' }} ****
            </span> 
          </v-col>
        </v-row>
        <v-hover v-slot:default="{ hover }">
          <v-row>
            <v-col>
              <span class="font-weight-medium">Base Location: </span>
              <span v-if="employee.city">{{ employee.city.name }}, {{ employee.city.state.name }}</span>
              <span
                v-if="canEditEmployeeCity"
                v-show="hover"
              >
                <employee-cities-dialog-edit
                  :employee="employee"
                  @updated="loadEmployee"
                />
              </span>
            </v-col>
          </v-row>
        </v-hover>

        <v-row v-if="!edit">
          <v-col v-if="!isResigned">           
            <div>
              <span class="font-weight-medium">On Leave:</span> 
              <span 
                :class="[employee.is_active ? 'error--text text--darken-2': 'success--text text--darken-2']"
              > {{ employee.is_active ? 'No' : 'Yes' }} </span>
            </div>
          </v-col>
        </v-row>
        <v-row v-else>
          <v-col
            v-if="!isResigned"
            cols="12"
          >
            <v-btn-toggle
              v-model="toggle"
            >
              <v-btn
                class="px-0"
                small
                active-class="primary lighten-1"
              >
                <v-icon :color="toggle === 0 ? 'white' : 'grey'">
                  mdi-check-circle
                </v-icon>
              </v-btn>
              <v-btn
                class="px-0"
                small
                active-class="primary lighten-1"
              >
                <v-icon :color="toggle === 1 ? 'white' : 'grey'">
                  mdi-clock {{ toggle == 1? 'mdi-spin':'' }}
                </v-icon>
              </v-btn>
            </v-btn-toggle>
            On Leave
          </v-col>
          <v-col
            v-if="toggle === 1"
            class="py-0" 
            cols="12" 
            lg="6"
          >
            <v-menu
              id="date"
              v-model="leaveStartMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              lazy
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="leaveStartDateDisplay"
                  label="Leave Start Date"
                  prepend-icon="event"
                  readonly
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="leaveForm.start_date"
                class="datepicker"
                no-title
                @input="leaveStartMenu = false"
              />
            </v-menu>
          </v-col>
          <v-col
            v-if="toggle === 1"
            class="py-0" 
            cols="12" 
            lg="6"
          >
            <v-menu
              id="date"
              v-model="leaveEndMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              lazy
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="leaveEndDateDisplay"
                  label="Leave End Date"
                  prepend-icon="event"
                  readonly
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="leaveForm.end_date"
                class="datepicker"
                no-title
                @input="leaveEndMenu = false"
              />
            </v-menu>
          </v-col>
        </v-row>
        <v-row>
          <v-col
            cols="12" 
            lg="6"
          >
            <div v-if="!edit">
              <span class="font-weight-medium">Joined Date:</span> 
              <span> {{ joinDateDisplay }} </span>
            </div>
            <v-menu
              v-else
              id="date"
              v-model="startmenu"
              :close-on-content-click="false"
              :nudge-right="40"
              lazy
              transition="scale-transition"
              offset-y
              full-width
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="joinDateDisplay"
                  label="Join Date"
                  prepend-icon="event"
                  readonly
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="form.joined_date"
                class="datepicker"
                no-title
                @input="startmenu = false"
              />
            </v-menu>
          </v-col>
        </v-row> 

        <v-row>
          <v-col cols="12">
            <v-hover v-slot:default="{ hover }">
              <v-row>
                <v-col>
                  <span class="font-weight-medium">Confirmed Date: </span>
                  <span> {{ confirmedDateDisplay }} </span> <!--keep -->
                
                  <span v-show="hover">
                    <employee-confirm-date-dialog-edit
                      v-if="!isMyProfile && $hasPermissionTo(['update employment section'])"
                      :employee="employee"
                      @updated="loadEmployee"
                    />
              
                  </span>
                </v-col>
              </v-row>
            </v-hover>
          </v-col>
        </v-row> 

        <v-card-actions v-if="canUpdateEmployment">
          <v-spacer />
          <div 
            v-if="!edit" 
            v-show="!isMyProfile"
          >
            <v-menu
              :offset-y="true"
              bottom
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  color="purple custom-btn"
                  dark
                  v-on="on"
                >
                  Actions
                  <v-icon 
                    right
                  >
                    mdi-menu-down
                  </v-icon>
                </v-btn>
              </template>

              <v-list>
                <v-list-item
                  v-for="(item, index) in filteredItems" 
                  :key="index"
                  @click="menuAction(index)"
                >
                  <v-list-item-title
                    v-text="item.title"
                  />
                </v-list-item>
              </v-list>
            </v-menu>
            <employee-separations-dialog-create
              ref="separation"
              :employee-uid="employeeUid"
              @created="loadEmployee"
            />
            <v-dialog 
              v-model="extendDialog" 
              max-width="450px"
            >
              <material-card
                :offset="10"
                :full-width="true"
                color="green"
                title="Rejoin Company"
                text="Confirmation To Rejoin Company"
                class="mb-0"
              >
                <v-container>
                  <v-row>
                    <v-col cols="12">
                      <v-card-text class="subtitle-2">
                        Are you sure to let the employee rejoin the company?
                      </v-card-text>
                    </v-col>
                  </v-row>
                </v-container>
                <v-card-actions>
                  <div class="flex-grow-1" />
                  <v-spacer />
                  <v-btn 
                    text 
                    @click="extendDialog = false"
                  >
                    No
                  </v-btn>
                  <v-btn
                    :loading="progressing" 
                    color="success" 
                    dark 
                    @click="extendService"
                  >
                    Yes
                  </v-btn>
                </v-card-actions>
              </material-card>
            </v-dialog>
          </div>
          <div v-else>
            <v-btn
              :disabled="progressing"
              text
              @click="edit = false"
            >
              Cancel
            </v-btn>
            <v-btn          
              :loading="progressing"
              color="success"
              @click="submitForm()"
            >
              Update
            </v-btn>
          </div>
        </v-card-actions>
      </v-skeleton-loader>
    </v-container>     
  </v-card>
</template>

<script>
  export default {
    components: {
      EmployeeSeparationsDialogList: () => import('@/components/employee/separations/DialogList'),
      EmployeeSeparationsDialogCreate: () => import('@/components/employee/separations/DialogCreate'),
      EmployeeCitiesDialogEdit: () => import('@/components/employee/cities/DialogEdit'),
      EmployeeConfirmDateDialogEdit: () => import('@/components/employee/confirm-date/DialogEdit'),
    },
    props: {
      employeeUid: {
        type: String,
        default: '',
      },    
    },
    data () {
      return {
        separationCount: 0,
        edit: false,
        skeleton: true,
        progressing: false,      
        startmenu: false,
        leaveStartMenu: false,
        leaveEndMenu: false,
        toggle: null,
        items: [
          { title: 'Edit' },
          { title: 'Resign' },
          { title: 'Rejoin' },        
        ],
        resignDialog: false,
        extendDialog: false,
        employee: {},
        tempLeave: {},
        form: {
          is_active: 0,
          joined_date: null,
        },
        leaveForm: {
          start_date: null,
          end_date: null,
        },
      }
    },
    computed: {
      canEditEmployeeCity () {
        return !this.isMyProfile && this.$hasPermissionTo(['update employment section']) && this.$hasPermissionTo(['update employee cities section'])
      },
      canUpdateEmployment () {
        return !this.isMyProfile && this.$hasPermissionTo(['update employment section'])
      },
      joinDateDisplay () {
        return this.edit ? this.$moment(this.form.joined_date, 'YYYY-MM-DD').format('D MMM YYYY') : this.$moment(this.employee.joined_date, 'YYYY-MM-DD').format('D MMM YYYY')
      },
      confirmedDateDisplay () {
        return this.employee.confirmed_date ? this.$moment(this.employee.confirmed_date, 'YYYY-MM-DD').format('D MMM YYYY') : 'N/A'
      },
      leaveStartDateDisplay () {
        return this.$moment(this.leaveForm.start_date, 'YYYY-MM-DD').format('D MMM YYYY')
      },
      leaveEndDateDisplay () {
        return this.$moment(this.leaveForm.end_date, 'YYYY-MM-DD').format('D MMM YYYY')
      },
      formIsActive () {
        if (this.toggle === 0) return 0 // inverted, used as on leave

        if (this.toggle === 1 || this.toggle === undefined) return 1

        return this.employee.is_active
      },
      finalLeaveForm () {
        return this.toggle === 1 ? this.leaveForm : {}
      },
      isMyProfile () {
        return this.$store.state.isMyProfile
      },
      isResigned () {
        return this.employee.end_date !== null && this.employee.end_date <= this.$moment().format('YYYY-MM-DD')
      },
      filteredItems () {
        if (this.employee.end_date && this.separationCount > 0) {
          return this.items.filter(item => {
            return item.title !== 'Resign'
          })
        }
        
        return this.items
      },
    },
    watch: {
      employeeUid: {
        immediate: true,
        handler (value) {
          if (value) {
            this.$resetData()
            this.loadEmployee()
          }
        },
      },
    },
    methods: {  
      submitForm () {
        this.progressing = true

        this.$api.employees().update(this.employeeUid, this.prepareFormData())
          .then((res) => {
            if (res.status >= 200 && res.status < 300) {
              this.updated()           

              this.$snack({
                message: res.data.status,
              })

              this.submitLeaveForm()
            }
          }).catch(err => {
            if (err.response.status === 400) {
              if (err.response.data.customers) {
                this.$dialog({
                  title: 'Active Customers',
                  message: 'This user has active customers. Please reassign them',
                  color: 'error',
                })
              }
            }
          }).then(() => {
            this.edit = false
            this.extendDialog = false
            this.resignDialog = false
            this.progressing = false
          })
      },
      submitLeaveForm () {
        this.$api.employeeTempLeaves().update(this.employeeUid, this.finalLeaveForm)
          .then(res => {
            this.loadEmployee()
          })
          .catch(() => {})
      },
      loadEmployee () {
        Promise.all([
          this.$api.employees().show(this.employeeUid),
          this.$api.employeeTempLeaves().list(this.employeeUid),
          this.$api.employeeSeparations().list(this.employeeUid),
        ])
          .then(([employee, tempLeave, separationHistory]) => {
            this.employee = employee.data        
            this.tempLeave = tempLeave.data
            this.separationCount = separationHistory.data.length
            
            this.skeleton = false
          })
      },
      editForm () {
        this.form.is_active = !this.employee.is_active
        this.form.joined_date = this.employee.joined_date || this.$moment().format('YYYY-MM-DD')

        this.leaveForm.start_date = this.tempLeave.start_date || this.$moment().format('YYYY-MM-DD')
        this.leaveForm.end_date = this.tempLeave.end_date || this.$moment().format('YYYY-MM-DD')

        if (!_.isEmpty(this.tempLeave)) {
          this.toggle = 1
        } else {
          this.toggle = this.employee.is_active ? null : 0
        }

        this.edit = true
      },
      extendService () {
        this.form.end_date = null
        this.form.is_active = !this.employee.is_active
        this.form.joined_date = this.employee.joined_date || this.$moment().format('YYYY-MM-DD')
        this.submitForm()
      },      
      menuAction (index) {
        if (index === 0) {
          this.editForm()
        } else if (index === 1) {
          this.$refs.separation.openDialog()
        } else if (index === 2) {
          this.extendDialog = true
        }
      },
      prepareFormData () {
        return { ...this.form, is_active: this.formIsActive }
      },
      updated () {
        this.$set(this.employee, 'is_active', this.formIsActive)
        this.$set(this.employee, 'joined_date', this.form.joined_date)
        this.$set(this.employee, 'end_date', this.form.end_date)
      },
    },
  }
</script>

<style lang="css" scoped>
  ::v-deep .datepicker .v-btn{
      padding:0 !important;
      border-radius: 50%;
  }
.v-btn.custom-btn:hover{
  box-shadow: 2px 5px 15px 2px rgba(0, 0, 0, 0.267);
  transform: none;
}

</style>
